import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Button, ValidationMessage, Input } from "components";
import { Link } from "react-router-dom";
import { resetPassword } from "aws-amplify/auth";
import { StepState } from "helpers";
import { useTranslation } from "react-i18next";

interface AuthPasswordResetFormProps extends Pick<StepState, "setStep"> {
  onEmailSent: (email: string) => void;
}

export const AuthPasswordResetForm = ({ setStep, onEmailSent }: AuthPasswordResetFormProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(true);

  useEffect(() => {
    const emailRegex = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{1,}$/i;
    setDisabled(!emailRegex.test(email));
    if (error) setError("");
  }, [email]);

  const onChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => setEmail(value);

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setDisabled(true);

    try {
      const { nextStep } = await resetPassword({ username: email });
      console.log("Reset password response:", nextStep);

      if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        onEmailSent(email);
        setStep(1);
      } else {
        throw new Error(`Unexpected reset password step: ${nextStep.resetPasswordStep}`);
      }
    } catch (err: any) {
      console.error("Reset password error:", err);
      const errorMessages: Record<string, string> = {
        UserNotFoundException: "No account found with this email address.",
        LimitExceededException: "Too many requests. Please try again later.",
        InvalidParameterException: "Invalid email format.",
      };
      setError(errorMessages[err.name] || err.message || "An error occurred during password reset");
    } finally {
      setDisabled(false);
    }
  };

  return (
    <form onSubmit={onSubmitHandler} autoComplete="off">
      <div className="space-y-8">
        <div className="flex flex-col">
          <Input
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={onChangeHandler}
            error={!!error}
            placeholder="example@example.com"
            autoComplete="off"
          />
          <ValidationMessage>{error}</ValidationMessage>
        </div>
        <div className="flex">
          <div className="flex flex-1 items-center justify-center">
            <Link to="/">{t("common.back")}</Link>
          </div>
          <div className="flex-1">
            <Button type="submit" disabled={disabled}>
              {t("auth.requestLink")}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
